import store from '@/store'
import { formatCurrencyForCalculations } from '@/utils/currency'
import { VERSIONS } from '@/utils/constants'
import Big from 'big.js'

export const priceCalculator = {
  /**
   * Calcula impuestos usando la lógica específica por país
   */
  calculateTaxes(subtotal, taxes) {
    const applicationVersion = store.get('shop/shop@config.applicationVersion')
    let totalTaxes = 0

    if (applicationVersion === VERSIONS.MEXICO) {
      let ivaPercentage = 0
      let iepsPercentage = 0

      taxes.forEach(tax => {
        if (tax.type === 'IEPS') {
          iepsPercentage = tax.percentage
        } else if (tax.type === 'IVA') {
          ivaPercentage = tax.percentage
        }
      })

      if (taxes.length === 2 && iepsPercentage > 0 && ivaPercentage > 0) {
        const iepsValue = subtotal * (iepsPercentage / 100)
        const ivaBase = subtotal + iepsValue
        const ivaValue = ivaBase * (ivaPercentage / 100)
        totalTaxes = iepsValue + ivaValue
      } else {
        totalTaxes = taxes.reduce((acc, tax) => {
          return acc + subtotal * (tax.percentage / 100)
        }, 0)
      }
    } else {
      totalTaxes = taxes.reduce((acc, tax) => {
        return acc + subtotal * (tax.percentage / 100)
      }, 0)
    }

    return totalTaxes
  },

  /**
   * Calcula el precio individual con descuentos e impuestos (incluye cupón si se proporciona)
   */
  calculatePriceWithDiscountAndTaxes(product, appliedCoupon = null) {
    // Productos con finalPrice preservado (del carrito) - usa valores consistentes
    if (product.finalPrice !== undefined && !appliedCoupon) {
      return formatCurrencyForCalculations(Big(product.finalPrice).times(product.quantity)
      )
    }

    // Solo para casos especiales: cupones o productos sin finalPrice
    let productDiscount = product.discounts?.discountPercentage || 0
    productDiscount = productDiscount ? productDiscount / 100 : 0

    let couponDiscount = appliedCoupon?.discountPercentage || 0
    couponDiscount = couponDiscount ? couponDiscount / 100 : 0

    const totalDiscounts = productDiscount + couponDiscount
    const basePrice = product.price || 0
    const discountValue = basePrice * product.quantity * totalDiscounts
    const subtotalWithDiscount = basePrice * product.quantity - discountValue

    let totalTaxes = 0
    if (product.taxes && product.taxes.length > 0) {
      totalTaxes = this.calculateTaxes(subtotalWithDiscount, product.taxes)
    }

    return formatCurrencyForCalculations(subtotalWithDiscount + totalTaxes)
  },

  /**
   * Calcula el precio individual sin descuentos pero con impuestos
   */
  calculatePriceWithTaxes(product) {
    // Si ya tiene finalPriceBeforeDiscounts calculado (productos del carrito)
    // Verificamos que no sea undefined Y que tenga finalPrice (doble validación)
    if (product.finalPriceBeforeDiscounts !== undefined && product.finalPrice !== undefined) {
      return formatCurrencyForCalculations(
        Big(product.finalPriceBeforeDiscounts).times(product.quantity)
      )
    }

    // Calcular desde cero (items de orden)
    const basePrice = product.price || 0
    const subtotalWithoutDiscount = basePrice * product.quantity

    let totalTaxes = 0
    if (product.taxes && product.taxes.length > 0) {
      totalTaxes = this.calculateTaxes(subtotalWithoutDiscount, product.taxes)
    }

    return formatCurrencyForCalculations(subtotalWithoutDiscount + totalTaxes)
  },

  /**
   * Verifica si un producto tiene descuentos aplicados (incluyendo cupón)
   */
  hasDiscount(product, appliedCoupon = null) {
    // Con precios preservados, casi todos los productos tendrán finalPrice/finalPriceBeforeDiscounts
    if (product.finalPrice !== undefined && product.finalPriceBeforeDiscounts !== undefined && !appliedCoupon) {
      return product.finalPriceBeforeDiscounts !== product.finalPrice
    }

    // Fallback para casos especiales (muy raro después de preservar precios)
    const hasProductDiscount = product.discounts && product.discounts.discountPercentage > 0
    const hasCouponDiscount = appliedCoupon && appliedCoupon.discountPercentage > 0

    return hasProductDiscount || hasCouponDiscount
  },

  /**
   * Calcula el subtotal de una lista de productos (con cupón opcional)
   */
  calculateSubtotal(products, appliedCoupon = null) {
    if (!products.length) return 0

    const totalPrice = products
      .map(product => this.calculatePriceWithDiscountAndTaxes(product, appliedCoupon))
      .reduce((acc, price) => formatCurrencyForCalculations(Big(price).plus(acc)), 0)

    return totalPrice
  },

  /**
   * Calcula el monto descontado por el cupón aplicado
   */
  calculateCouponDiscountAmount(products, appliedCoupon) {
    if (!products.length || !appliedCoupon) return 0

    const priceWithProductDiscounts = this.calculateSubtotal(products)
    const priceWithProductAndCouponDiscounts = this.calculateSubtotal(products, appliedCoupon)

    return formatCurrencyForCalculations(
      Big(priceWithProductDiscounts).minus(priceWithProductAndCouponDiscounts)
    )
  },

  /**
   * Función utilitaria para procesar productos con finalPrice calculado
   * Centraliza la lógica de procesamiento de precios finales
   */
  processProductWithFinalPrices(product) {
    const processedProduct = { ...product }

    // Si no tiene precios finales calculados, calcularlos
    if (processedProduct.finalPrice === undefined || processedProduct.finalPriceBeforeDiscounts === undefined) {
      const tempProduct = { ...processedProduct, quantity: 1 }

      // CRÍTICO: Calcular precios base SIN descuentos para finalPriceBeforeDiscounts
      const basePriceWithTaxes = this.calculateBasePriceWithTaxes(tempProduct)
      const finalPriceWithDiscounts = this.calculatePriceWithDiscountAndTaxes(tempProduct)

      processedProduct.finalPriceBeforeDiscounts = basePriceWithTaxes
      processedProduct.finalPrice = finalPriceWithDiscounts
    }

    return processedProduct
  },

  /**
   * Calcula precio base + impuestos SIN aplicar descuentos del producto
   */
  calculateBasePriceWithTaxes(product) {
    const basePrice = product.price || 0
    const subtotalWithoutDiscount = basePrice * product.quantity

    let totalTaxes = 0
    if (product.taxes && product.taxes.length > 0) {
      totalTaxes = this.calculateTaxes(subtotalWithoutDiscount, product.taxes)
    }

    return formatCurrencyForCalculations(subtotalWithoutDiscount + totalTaxes)
  }
}

export default priceCalculator
